<template>
  <v-container fluid class="tr-page pa-0">
    <section class="tr-hero">
      <div class="tr-hero__content">
        <v-avatar color="white" size="56" class="tr-hero__mark elevation-3">
          <v-icon color="primary" size="30">{{ isKids ? 'mdi-human-child' : 'mdi-golf' }}</v-icon>
        </v-avatar>
        <div>
          <span class="tr-eyebrow">Registro al torneo</span>
          <h1>{{ info ? info.name : 'Torneo' }}</h1>
          <p v-if="info">{{ dateRange }}</p>
        </div>
      </div>
    </section>

    <v-row justify="center" class="tr-content ma-0">
      <v-col cols="12" sm="10" md="8" lg="7" xl="6" class="px-4 px-sm-0">
        <!-- Cargando -->
        <v-card v-if="loading" class="tr-card elevation-4 text-center py-10">
          <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
        </v-card>

        <v-alert v-else-if="notFound" type="error" text class="tr-card">
          No encontramos este torneo. Verifica que el enlace sea el correcto.
        </v-alert>

        <!-- Registro completado -->
        <v-card v-else-if="result" class="tr-card elevation-4">
          <v-card-text class="pa-6 pa-sm-8 text-center">
            <v-avatar color="success" size="68" class="mb-4">
              <v-icon color="white" size="40">mdi-check</v-icon>
            </v-avatar>
            <h2 class="headline font-weight-bold primary--text mb-1">¡Registro completado!</h2>
            <p class="body-1 grey--text mb-6">{{ result.member.full_name }}</p>

            <div v-if="result.member.access_code" class="mb-6">
              <img :src="qrUrl(result.member.access_code)" alt="Código QR de acceso" class="tr-qr">
              <p class="text-caption grey--text mt-3 mb-0">
                Presenta este código QR el día del torneo. Si dejaste teléfono o correo, también te lo enviamos por ahí.
              </p>
            </div>

            <div v-if="people.length" class="tr-summary text-left pa-4 mb-6">
              <div class="subtitle-2 font-weight-bold primary--text mb-2">Registrados contigo</div>
              <div v-for="p in people" :key="p.id" class="text-body-2 py-1">
                {{ p.full_name }}<span v-if="p.age"> ({{ p.age }} años)</span>
                <span v-if="p.relationship" class="grey--text"> · {{ p.relationship }}</span>
              </div>
            </div>

            <v-btn
              v-if="canAddGuestsLater"
              color="primary"
              outlined
              block
              large
              depressed
              :to="{ name: 'partners_register', params: { inviter_token: result.member.inviter_token } }"
            >
              Agregar invitados más tarde
            </v-btn>
          </v-card-text>
        </v-card>

        <template v-else-if="info">
          <v-alert v-if="!info.is_registration_open" type="warning" text class="tr-card">
            {{ closedMessage }}
          </v-alert>

          <v-card v-else class="tr-card elevation-4">
            <v-card-text class="pa-5 pa-sm-6">
              <!-- Datos del torneo -->
              <p v-if="info.description" class="body-2 grey--text text--darken-1 mb-4" style="white-space: pre-line">{{ info.description }}</p>
              <div class="tr-summary mb-6">
                <div v-for="fact in facts" :key="fact.label" class="tr-summary__row">
                  <span>{{ fact.label }}</span><strong>{{ fact.value }}</strong>
                </div>
                <div v-if="info.registration_close_at" class="tr-summary__row">
                  <span>Inscripciones hasta</span><strong>{{ closeDate }}</strong>
                </div>
              </div>

              <v-form ref="form" @submit.prevent="submit">
                <h3 class="tr-heading">{{ isKids ? 'Datos del padre o tutor' : 'Tus datos' }}</h3>

                <div class="field-group">
                  <label class="field-label">Nombre completo <span>*</span></label>
                  <v-text-field v-model="form.full_name" outlined hide-details="auto" placeholder="Ej. Juan Perez Lopez" class="app-input" :rules="[rules.required]"></v-text-field>
                </div>
                <div class="field-group">
                  <label class="field-label">Teléfono (WhatsApp) <span v-if="isKids">*</span><em v-else>Opcional</em></label>
                  <v-text-field v-model="form.phone" outlined hide-details="auto" type="tel" placeholder="Ej. 4441234567" class="app-input" :rules="isKids ? [rules.required] : []"></v-text-field>
                  <small class="field-hint">Recibirás tu confirmación y QR por WhatsApp.</small>
                </div>
                <div class="field-group">
                  <label class="field-label">Correo electrónico <em>Opcional</em></label>
                  <v-text-field v-model="form.email" outlined hide-details="auto" type="email" placeholder="Ej. juan@correo.com" class="app-input" :rules="[rules.email]"></v-text-field>
                </div>
                <div class="field-group">
                  <label class="field-label">Club de origen <em>Opcional</em></label>
                  <v-text-field v-model="form.club" outlined hide-details="auto" placeholder="Ej. Club Campestre" class="app-input"></v-text-field>
                </div>
                <div v-if="!isKids && !info.handicap_free" class="field-group">
                  <label class="field-label">Handicap <em>{{ handicapHint }}</em></label>
                  <v-text-field v-model.number="form.handicap" outlined hide-details="auto" type="number" class="app-input"></v-text-field>
                </div>

                <!-- Niños -->
                <template v-if="isKids">
                  <h3 class="tr-heading mt-8">Jugadores</h3>
                  <div v-for="(child, i) in form.children" :key="'c' + i" class="tr-person pa-4 mb-3">
                    <div class="d-flex align-center mb-3">
                      <span class="subtitle-2 font-weight-bold">Niño {{ i + 1 }}</span>
                      <v-spacer></v-spacer>
                      <v-btn v-if="form.children.length > 1" icon small color="error" aria-label="Quitar niño" @click="form.children.splice(i, 1)">
                        <v-icon small>mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                    <v-text-field v-model="child.full_name" label="Nombre" outlined dense hide-details="auto" class="mb-3" :rules="[rules.required]"></v-text-field>
                    <v-row dense>
                      <v-col cols="5">
                        <v-text-field v-model.number="child.age" label="Edad" type="number" min="1" max="18" outlined dense hide-details="auto" :rules="[rules.age]"></v-text-field>
                      </v-col>
                      <v-col cols="7">
                        <v-select v-model="child.gender" :items="genders" label="Género" outlined dense hide-details="auto" :rules="[rules.required]"></v-select>
                      </v-col>
                    </v-row>
                  </div>
                  <v-btn v-if="form.children.length < maxGuests" block outlined color="primary" class="tr-add" @click="addChild">
                    <v-icon left>mdi-plus</v-icon>Agregar niño
                  </v-btn>
                  <div v-else class="text-caption grey--text">Máximo {{ maxGuests }} niños por registro.</div>
                </template>

                <!-- Invitados / acompañantes -->
                <h3 class="tr-heading mt-8">
                  {{ isKids ? 'Acompañantes' : 'Invitados' }}
                  <em class="tr-optional">Opcional</em>
                </h3>
                <div v-for="(guest, i) in form.guests" :key="'g' + i" class="tr-person pa-4 mb-3">
                  <div class="d-flex align-center mb-3">
                    <span class="subtitle-2 font-weight-bold">{{ isKids ? 'Acompañante' : 'Invitado' }} {{ i + 1 }}</span>
                    <v-spacer></v-spacer>
                    <v-btn icon small color="error" aria-label="Quitar" @click="form.guests.splice(i, 1)">
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                  <v-text-field v-model="guest.full_name" label="Nombre" outlined dense hide-details="auto" :class="{ 'mb-3': isKids }" :rules="[rules.required]"></v-text-field>
                  <v-text-field v-if="isKids" v-model="guest.relationship" label="Parentesco" outlined dense hide-details="auto" :rules="[rules.required]"></v-text-field>
                </div>
                <v-btn v-if="isKids || form.guests.length < maxGuests" block outlined color="primary" class="tr-add" @click="addGuest">
                  <v-icon left>mdi-plus</v-icon>{{ isKids ? 'Agregar acompañante' : 'Agregar invitado' }}
                </v-btn>
                <div v-else class="text-caption grey--text">Máximo {{ maxGuests }} invitados por jugador.</div>

                <v-alert v-if="error" type="error" dense text class="mt-5 mb-0">{{ error }}</v-alert>

                <v-btn type="submit" color="primary" block depressed large class="tr-submit mt-6" :loading="submitting">
                  Completar registro
                </v-btn>
              </v-form>
            </v-card-text>
          </v-card>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { publicTournamentsAPI } from '@/services/publicTournaments'

const ERROR_MESSAGES = {
  registration_closed: 'Las inscripciones de este torneo no están abiertas.',
  tournament_full: 'El torneo ya no tiene lugares disponibles.',
  duplicate_phone: 'Ya existe un registro con este teléfono.',
  duplicate_email: 'Ya existe un registro con este correo electrónico.',
  handicap_out_of_range: 'Tu handicap no está dentro del rango permitido para este torneo.',
  guest_limit: 'Superaste el máximo de invitados permitido.',
  children_limit: 'Superaste el máximo de niños permitido.'
}

export default {
  name: 'TournamentRegister',
  data () {
    return {
      loading: true,
      notFound: false,
      info: null,
      submitting: false,
      error: null,
      result: null,
      form: {
        full_name: '',
        phone: '',
        email: '',
        club: '',
        handicap: null,
        children: [],
        guests: []
      },
      genders: [
        { text: 'Niño', value: 'M' },
        { text: 'Niña', value: 'F' },
        { text: 'Otro', value: 'O' }
      ],
      rules: {
        required: v => (v !== null && v !== undefined && String(v).trim() !== '') || 'Campo requerido',
        email: v => !v || /.+@.+\..+/.test(v) || 'Correo no válido',
        age: v => (v >= 1 && v <= 18) || 'Edad entre 1 y 18'
      }
    }
  },
  computed: {
    slug () {
      return this.$route.params.slug
    },
    isKids () {
      return this.info && this.info.tournament_type === 'kids'
    },
    maxGuests () {
      return this.info ? this.info.max_guests_per_member : 0
    },
    dateRange () {
      const fmt = d => new Date(d + 'T00:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })
      if (this.info.start_date === this.info.end_date) return fmt(this.info.start_date)
      return `${fmt(this.info.start_date)} al ${fmt(this.info.end_date)}`
    },
    closeDate () {
      return new Date(this.info.registration_close_at).toLocaleString('es-MX', {
        day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
      })
    },
    handicapHint () {
      const i = this.info
      return i.handicap_min !== null && i.handicap_min !== undefined
        ? `Rango ${i.handicap_min} a ${i.handicap_max}`
        : 'Opcional'
    },
    facts () {
      const i = this.info
      const short = t => (t ? String(t).slice(0, 5) : '')
      const list = [
        { icon: 'mdi-calendar', label: 'Fecha', value: this.dateRange }
      ]
      if (i.start_time) {
        const range = i.end_time ? `${short(i.start_time)} – ${short(i.end_time)}` : short(i.start_time)
        list.push({
          icon: 'mdi-clock-outline',
          label: i.starting_shotgun ? 'Horario (escopetazo)' : 'Horario',
          value: i.starting_shotgun && i.shotgun_time ? `${range} · salida ${short(i.shotgun_time)}` : range
        })
      }
      if (i.courses && i.courses.length) {
        list.push({ icon: 'mdi-golf', label: 'Cancha', value: i.courses.map(c => c.name).join(', ') })
      }
      if (!i.handicap_free && i.handicap_min !== null && i.handicap_min !== undefined) {
        list.push({ icon: 'mdi-chart-line', label: 'Handicap', value: `${i.handicap_min} a ${i.handicap_max}` })
      }
      list.push({
        icon: 'mdi-account-multiple',
        label: 'Lugares disponibles',
        value: String(i.available_slots)
      })
      if (i.max_guests_per_member) {
        list.push({
          icon: 'mdi-account-plus-outline',
          label: this.isKids ? 'Niños por registro' : 'Invitados por jugador',
          value: `Hasta ${i.max_guests_per_member}`
        })
      }
      return list
    },
    closedMessage () {
      if (this.info.available_slots === 0) return 'El torneo ya no tiene lugares disponibles.'
      if (this.info.registration_open_at && new Date(this.info.registration_open_at) > new Date()) {
        return 'Las inscripciones aún no han abierto.'
      }
      return 'Las inscripciones de este torneo están cerradas.'
    },
    people () {
      if (!this.result) return []
      return [...(this.result.children || []), ...(this.result.guests || [])]
    },
    canAddGuestsLater () {
      return !this.isKids && this.result && this.result.member.inviter_token &&
        this.result.guests.length < this.maxGuests
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    async load () {
      try {
        const { data } = await publicTournamentsAPI.getInfo(this.slug)
        this.info = data
        if (data.tournament_type === 'kids') this.addChild()
      } catch (err) {
        this.notFound = true
      } finally {
        this.loading = false
      }
    },
    addChild () {
      this.form.children.push({ full_name: '', age: null, gender: '' })
    },
    addGuest () {
      this.form.guests.push(this.isKids ? { full_name: '', relationship: '' } : { full_name: '' })
    },
    qrUrl (code) {
      return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=${encodeURIComponent(code)}`
    },
    async submit () {
      this.error = null
      if (!this.$refs.form.validate()) return
      if (!this.isKids && !this.form.phone && !this.form.email) {
        this.error = 'Indica un teléfono o un correo para enviarte tu confirmación.'
        return
      }

      const payload = {
        full_name: this.form.full_name,
        phone: this.form.phone,
        email: this.form.email,
        club: this.form.club,
        guests: this.form.guests.filter(g => g.full_name && g.full_name.trim())
      }
      if (!this.isKids && this.form.handicap !== null && this.form.handicap !== '') {
        payload.handicap = this.form.handicap
      }
      if (this.isKids) payload.children = this.form.children

      this.submitting = true
      try {
        const { data } = await publicTournamentsAPI.register(this.slug, payload)
        this.result = data
        window.scrollTo(0, 0)
      } catch (err) {
        this.error = this.errorMessage(err)
      } finally {
        this.submitting = false
      }
    },
    errorMessage (err) {
      const res = err.response
      if (!res) return 'No hay conexión. Intenta de nuevo.'
      if (res.status === 429) return 'Demasiados intentos. Espera un minuto e intenta de nuevo.'
      const d = res.data || {}
      if (d.code && ERROR_MESSAGES[d.code]) return ERROR_MESSAGES[d.code]
      if (d.message) return d.message
      if (d.error) return d.error
      if (d.details) return 'Revisa los datos del formulario.'
      return 'No se pudo completar el registro. Intenta de nuevo.'
    }
  }
}
</script>

<style scoped>
.tr-page { min-height: 100vh; padding-bottom: 3rem; background: #f4f3f7; }
.tr-hero { padding: 2rem 1.5rem 4.5rem; color: white; background: #2a2738; }
.tr-hero__content { display: flex; align-items: center; width: 100%; max-width: 670px; margin: 0 auto; }
.tr-hero__mark { flex: 0 0 auto; margin-right: 1rem; }
.tr-eyebrow { display: block; color: #d9d5ed; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.tr-hero__content h1 { margin: 0.25rem 0; font-size: 1.55rem; line-height: 1.2; }
.tr-hero__content p { margin: 0; color: #e5e2ef; font-size: 0.9rem; }
.tr-content { margin-top: -2.5rem !important; }
.tr-card { overflow: hidden; border-radius: 8px !important; }
.tr-heading { margin-bottom: 1rem; color: #2a2738; font-size: 1rem; font-weight: 700; }
.tr-optional { color: #777280; font-size: 0.75rem; font-style: normal; font-weight: 400; margin-left: 0.25rem; }
.field-group { margin-bottom: 1.25rem; }
.field-label { display: block; margin-bottom: 0.45rem; color: #45424f; font-size: 0.82rem; font-weight: 700; }
.field-label span { color: #d64156; }
.field-label em { color: #777280; font-size: 0.75rem; font-style: normal; font-weight: 400; }
.field-hint { display: block; margin-top: 0.45rem; color: #777280; font-size: 0.75rem; }
::v-deep .app-input .v-input__slot { min-height: 52px !important; margin-bottom: 0; }
::v-deep .app-input .v-text-field__slot input { font-size: 1rem; }
.tr-person { border: 1px solid #e4e2e9; border-radius: 8px; background: #faf9fc; }
.tr-add { height: 48px !important; border-style: dashed; letter-spacing: 0 !important; }
.tr-submit { height: 52px !important; letter-spacing: 0 !important; }
.tr-summary { border-radius: 8px; background: #f4f3f7; padding: 0.25rem 1rem; }
.tr-summary__row { display: flex; justify-content: space-between; gap: 1rem; padding: 0.65rem 0; color: #686370; font-size: 0.9rem; }
.tr-summary__row + .tr-summary__row { border-top: 1px solid #e4e2e9; }
.tr-summary__row strong { color: #2a2738; overflow-wrap: anywhere; text-align: right; }
.tr-qr { width: 220px; height: 220px; border: 1px solid #e4e2e9; border-radius: 8px; }
@media (min-width: 600px) {
  .tr-hero { padding-top: 2.75rem; }
  .tr-hero__content h1 { font-size: 1.9rem; }
  .tr-content { margin-top: -2.75rem !important; }
}
</style>
